/**
 * Scene Base - Abstract base class for all game scenes
 */

import { Scene } from "@babylonjs/core/scene";
import { Engine } from "../core/Engine";
import { IDisposable, IUpdatable } from "../core/Engine.types";

export abstract class SceneBase implements IUpdatable, IDisposable {
    protected _engine: Engine;
    protected _scene: Scene;
    protected _isInitialized: boolean = false;

    public get babylonScene(): Scene {
        return this._scene;
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    constructor(engine: Engine) {
        this._engine = engine;
        this._scene = new Scene(engine.babylonEngine);
    }

    /**
     * Initialize the scene - called once before first activation
     * Override to set up scene contents
     */
    public async initialize(): Promise<void> {
        if (this._isInitialized) return;
        
        await this.onInitialize();
        this._isInitialized = true;
    }

    /**
     * Called when scene is activated (switched to)
     */
    public async onActivate(): Promise<void> {
        // Override in subclass
    }

    /**
     * Called when scene is deactivated (switched away from)
     */
    public async onDeactivate(): Promise<void> {
        // Override in subclass
    }

    /**
     * Update loop - called every frame when active
     */
    public update(deltaTime: number): void {
        this.onUpdate(deltaTime);
    }

    /**
     * Dispose the scene and all resources
     */
    public dispose(): void {
        this.onDispose();
        this._scene.dispose();
        this._isInitialized = false;
    }

    /**
     * Override to initialize scene contents
     */
    protected abstract onInitialize(): Promise<void>;

    /**
     * Override for custom update logic
     */
    protected onUpdate(_deltaTime: number): void {
        // Override in subclass
    }

    /**
     * Override for custom disposal logic
     */
    protected onDispose(): void {
        // Override in subclass
    }
}
